import React, { useState, useEffect, useRef } from 'react';
import ReusableTable from '../../dashboard/ReusableTable';
import { getRequest, postRequest } from '../../services/apiService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReusableForm from '../../common/ReusableForm';
import ReusableDeleteDialog from '../../common/ReusableDeleteDialog';
import { Dropdown } from 'primereact/dropdown';

const DirectrateManagement = () => {
  const [directrates, setDirectrates] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentDirectrate, setCurrentDirectrate] = useState(null);
  const [directrateToDelete, setDirectrateToDelete] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    block: null,
    pallet: '',
    stage: ''
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [selectedBlock, setSelectedBlock] = useState('');
  const [search, setSearch] = useState("");
  const searchTimeout = useRef(null);

  const [deleteFormData, setDeleteFormData] = useState({ confirmation: '' });
  
  const deleteFormFields = [
    {
      name: "confirmation",
      label: "Confirmation",
      type: "text",
      required: true,
      placeholder: "Type 'DELETE' to confirm",
      helpText: "This action cannot be undone. Type 'DELETE' to confirm deletion."
    }
  ];

  const formFields = [
    { name: 'name', label: 'Unit Name', type: 'text', required: true },
    { name: 'description', label: 'Description', type: 'text' },
    { name: 'block', label: 'Block', type: 'select', required: true, options: blocks.map(b => ({ label: b.name, value: b.id })) },
    { name: 'pallet', label: 'Pallet', type: 'text' },
    { name: 'stage', label: 'Stage', type: 'text' }
  ];

  const fetchDirectrates = async (page = 1) => {
    setLoading(true);
    try {
      let url = `master/mpr-unit-master/?page=${page}`;
      if (selectedBlock) {
        url += `&block=${selectedBlock}`;
      }
      const response = await getRequest(url);
      const directratesArray = response.results?.data || [];
      setDirectrates(directratesArray);
      setTotalRecords(response.count || 0);
    } catch (error) {
      toast.error('Failed to fetch unit.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDirectrates(currentPage);
    fetchBlocks();
  }, [currentPage, selectedBlock]);

  const blockOptions = [
    { label: "", value: "" },
    ...blocks.map(block => ({ label: block.name, value: block.id }))
  ];

  const fetchBlocks = async () => {
    try {
      let allBlocks = [];
      let currentPage = 1;
      let hasMorePages = true;
      while (hasMorePages) {
        const response = await getRequest(`master/mpr-block-master/?page=${currentPage}`);
        let pageBlocks = [];
        if (response.results?.data) {
          pageBlocks = response.results.data;
        } else if (response.data) {
          pageBlocks = response.data;
        }
        allBlocks = [...allBlocks, ...pageBlocks];
        if (response.next) {
          currentPage++;
        } else {
          hasMorePages = false;
        }
      }
      setBlocks(allBlocks);
    } catch (error) {
      toast.error('Failed to fetch blocks.');
    }
  };

  const handleSaveDirectrate = async () => {
    try {
      let payload = {
        name: formData.name,
        description: formData.description,
        block: formData.block,
        pallet: formData.pallet,
        stage: formData.stage
      };

      if (editMode && currentDirectrate) {
        // Update using POST with ID
        payload = {
          ...payload,
          id: currentDirectrate.id
        };
        await postRequest('master/mpr-unit-master/', payload);
        toast.success('Unit updated successfully!');
      } else {
        // Create new
        await postRequest('master/mpr-unit-master/', payload);
        toast.success('Unit added successfully!');
        setCurrentPage(1);
      }
      await fetchDirectrates(currentPage);
      setShowFormModal(false);
      setEditMode(false);
      setCurrentDirectrate(null);
      setFormData({ name: '', description: '', block: null, pallet: '', stage: '' });
    } catch (error) {
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to save unit.');
      }
    }
  };

  const openAddModal = () => {
    setFormData({ name: '', description: '', block: null, pallet: '', stage: '' });
    setEditMode(false);
    setCurrentDirectrate(null);
    setShowFormModal(true);
  };

  const openEditModal = (directrate) => {
    setCurrentDirectrate(directrate);
    setFormData({
      name: directrate.name,
      description: directrate.description,
      block: directrate.block,
      pallet: directrate.pallet,
      stage: directrate.stage
    });
    setEditMode(true);
    setShowFormModal(true);
  };

  const openDeleteModal = (directrate) => {
    setDirectrateToDelete(directrate);
    setDeleteFormData({ confirmation: '' });
    setShowDeleteModal(true);
  };

  const deleteDirectrate = async () => {
    if (deleteFormData.confirmation !== "DELETE") {
      toast.error('Please type "DELETE" to confirm');
      return;
    }
    try {
      // Soft delete using POST with status_db:3
      await postRequest('master/mpr-unit-master/', {
        id: directrateToDelete.id,
        status_db: 3
      });
      toast.success('Unit deleted successfully!');
      await fetchDirectrates(currentPage);
      setShowDeleteModal(false);
      setDirectrateToDelete(null);
      setDeleteFormData({ confirmation: '' });
    } catch (error) {
      toast.error('Failed to delete unit.');
    }
  };

  const onPageChange = (event) => {
    setCurrentPage(event.page + 1);
    setRowsPerPage(event.rows);
  };

  const tableColumns = [
    { field: 'name', header: 'Name' },
    { field: 'description', header: 'Description' },
    { 
      field: 'block', 
      header: 'Block',
      body: (rowData) => {
        const block = blocks.find(b => b.id === rowData.block);
        return block ? block.name : '-';
      }
    },
    { field: 'pallet', header: 'Pallet' },
    { field: 'stage', header: 'Stage' }
  ];

  const actionItems = [
    {
      label: 'Edit',
      icon: 'pi pi-pencil',
      command: (rowData) => openEditModal(rowData),
    },
    {
      label: 'Delete',
      icon: 'pi pi-trash',
      command: (rowData) => openDeleteModal(rowData),
    },
  ];

  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      setLoading(true);
      let url = `master/mpr-unit-master/?search=${encodeURIComponent(search)}&page=${currentPage}`;
      if (selectedBlock) {
        url += `&block=${selectedBlock}`;
      }
      getRequest(url)
        .then(response => {
          const directratesArray = response.results?.data || [];
          setDirectrates(directratesArray);
          setTotalRecords(response.count || 0);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 700); // 700ms debounce
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [search, currentPage, selectedBlock]);

  const handleTableSearch = (value) => {
    setSearch(value);
  };

  return (
    <div className="bg-gray-100 py-8 px-4">
      <ToastContainer />
      <div className="max-w-full mx-auto">
        <div className="mb-4 flex justify-between items-center">
          <div className="flex-1">
            {/* Left side - can be used for other elements if needed */}
          </div>
          <div className="w-full md:w-64">
            <Dropdown
              value={selectedBlock}
              options={blockOptions}
              onChange={(e) => {
                setSelectedBlock(e.value);
                setCurrentPage(1);
              }}
              placeholder="Filter by Block"
              className="w-full"
            />
          </div>
        </div>
        
        <ReusableTable
          title="Unit Details"
          data={directrates}
          setData={setDirectrates}
          columnFields={tableColumns}
          setAddNewDialogVisible={openAddModal}
          actionMenu={true}
          actionItems={actionItems}
          loading={loading}
          totalRecords={totalRecords}
          rows={rowsPerPage}
          onPageChange={onPageChange}
          first={(currentPage - 1) * rowsPerPage}
          searchValue={search}
          onSearchChange={handleTableSearch}
        />
        
        {directrates.length === 0 && !loading && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No unit found. Add a new unit to get started.</p>
          </div>
        )}
      </div>

      {/* Add/Edit Modal with explicit wrapper */}
      {showFormModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full mx-auto max-w-lg animate-fade-in">
            <ReusableForm
              headerText={editMode ? "Edit Unit" : "Add Unit"}
              formData={formData}
              setFormData={setFormData}
              fields={formFields}
              onSubmit={handleSaveDirectrate}
              onHide={() => setShowFormModal(false)}
              loading={loading}
            />
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ReusableDeleteDialog
        visible={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onDelete={deleteDirectrate}
        fields={deleteFormFields}
        formData={deleteFormData}
        setFormData={setDeleteFormData}
        loading={loading}
        title="Delete Unit"
        description={`Are you sure you want to delete unit "${directrateToDelete?.name}"? This action cannot be undone. Type 'DELETE' to confirm.`}
        deleteLabel="Delete"
      />
    </div>
  );
};

export default DirectrateManagement;